import { mayAutoRefresh } from "./nightActivity.mjs";
import { createContext, useContext, useCallback, useEffect, useRef, useState } from "react";

const ConversationPresenceContext = createContext(null);
export const ConversationPresenceProvider = ConversationPresenceContext.Provider;

const samePresence = (left, right) => {
  if (!left || !right) return left === right;
  return Number(left.storeId) === Number(right.storeId)
    && left.phone === right.phone && left.module === right.module;
};

// The reporter must not change identity when the app or its history loader
// renders again. Otherwise child cleanup -> report -> parent render is a loop.
export function useActiveConversationPresence({ tab, historyLoaderRef }) {
  const [activeConversation, setActiveConversation] = useState(null);
  const currentTab = useRef(tab);
  const reportedTab = useRef(null);
  currentTab.current = tab;
  const reportConversation = useCallback(active => {
    reportedTab.current = currentTab.current;
    setActiveConversation(previous => samePresence(previous, active) ? previous : active);
    if (active?.storeId && active?.phone) void historyLoaderRef.current?.(active);
  }, [historyLoaderRef]);
  useEffect(() => {
    // A newly mounted conversation reports before the parent's passive effect.
    // Do not erase that new presence when clearing the previous screen's scope.
    if (reportedTab.current !== tab) setActiveConversation(null);
  }, [tab]);
  return { activeConversation, reportConversation };
}

// Only scalar conversation identity controls registration. Fresh records,
// updated message lists, and callback replacement are not conversation exits.
export function useConversationPresence({ phone, storeId, module, onConversationActive }) {
  const sharedReporter = useContext(ConversationPresenceContext);
  const callback = useRef(onConversationActive || sharedReporter);
  callback.current = onConversationActive || sharedReporter;
  const resolvedStoreId = Number(storeId);
  useEffect(() => {
    if (!phone || !resolvedStoreId) return undefined;
    callback.current?.({ phone, storeId:resolvedStoreId, module });
    return () => callback.current?.(null);
  }, [phone, resolvedStoreId, module]);
}

export function usePushPresenceHeartbeat({
  conversation, userId, userStoreId, userRole, isDemoMode, sendPresence,
}) {
  const phone = conversation?.phone;
  const storeId = Number(conversation?.storeId || userStoreId);
  useEffect(() => {
    if (!phone || !storeId || userId == null || userRole !== "consultant" || isDemoMode) return undefined;
    const scope = { phone, storeId, consultantId:userId };
    const beat = () => {
      if (document.visibilityState === "visible" && document.hasFocus() && mayAutoRefresh()) sendPresence({ ...scope, active:true });
    };
    beat();
    const interval = setInterval(beat, 30000);
    const onVisibility = () => { if (document.visibilityState === "visible") beat(); };
    const stop = () => sendPresence({ ...scope, active:false });
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", stop);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", stop);
      stop();
    };
  }, [phone, storeId, userId, userRole, isDemoMode, sendPresence]);
}
